/* eslint-disable custom-rules/modal-and-drawer-requires-title */
import { useMediaQuery } from '@uhg-abyss/web/hooks/useMediaQuery';
import { Button } from '@uhg-abyss/web/ui/Button';
import { Drawer } from '@uhg-abyss/web/ui/Drawer';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { RadioGroup } from '@uhg-abyss/web/ui/RadioGroup';
import { Text } from '@uhg-abyss/web/ui/Text';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { adobeLinkTrackEvent } from '../../AdobeTagging/adobeLinkTrackEvent';
import { Constants, PhysicianDirectorySortOrder } from '../../Constants';
import { notMobile, phoneOnly } from '../../ConstantsStyles';
import { handleLinkAndModalTrack } from '../../Utils/adobeTrackUtils';
import {
  DesktopSortText,
  MobileText,
  StyledPopover,
  radioGroupStyles,
  swapIconStyles,
} from './PhysicianDirectory.styled';

const SortingRadioGroup = ({
  setSortOrder,
  sortOrder,
  setIsOpen,
}: {
  setSortOrder: (val: PhysicianDirectorySortOrder) => void;
  sortOrder: PhysicianDirectorySortOrder;
  setIsOpen: (val: boolean) => void;
}) => {
  const { t } = useTranslation();
  const [temporarySortOrder, setTemporarySortOrder] =
    useState<PhysicianDirectorySortOrder>(sortOrder);
  const sortBtnName: string = 'sort-button';
  const azSortingList: string = 'az-sorting-list';

  useEffect(() => {
    const linkName: string = 'Physician sort button';
    const linkLocation: string = `body:${Constants.FACILITY_DETAILS.PHYSICIAN_DIRECTORY}`;
    const modalName: string = `Physician sort`;
    handleLinkAndModalTrack(linkName, linkLocation, modalName);
  }, []);
  return (
    <React.Fragment>
      <RadioGroup
        css={radioGroupStyles}
        data-auto-testid={azSortingList}
        data-testid={azSortingList}
        label={t('SORTBY')}
        onChange={(e: React.ChangeEvent<any>) => {
          setTemporarySortOrder(e.target.value);
          adobeLinkTrackEvent({
            name: `${azSortingList} ${e.target.value}`,
            location: `modal:physician sort`,
            type: 'internal',
            destinationUrl: '',
          });
        }}
        value={temporarySortOrder}
      >
        <RadioGroup.Radio
          css={{
            'abyss-radio-label': {
              color:
                temporarySortOrder === PhysicianDirectorySortOrder.ASCENDING
                  ? '#323334'
                  : '#4B4D4F',
              fontWeight:
                temporarySortOrder === PhysicianDirectorySortOrder.ASCENDING
                  ? 700
                  : 500,
            },
            'abyss-radio-root': {
              padding: '10px 0',
            },
          }}
          label={t(
            'FACILITY_DETAILS.PHYSICIAN_DIRECTORY_TAB.SORT_POPOVER.PROVIDER_ASCENDING'
          )}
          value={PhysicianDirectorySortOrder.ASCENDING}
        />
        <RadioGroup.Radio
          css={{
            'abyss-radio-label': {
              color:
                temporarySortOrder === PhysicianDirectorySortOrder.DESCENDING
                  ? '#323334'
                  : '#4B4D4F',
              fontWeight:
                temporarySortOrder === PhysicianDirectorySortOrder.DESCENDING
                  ? 700
                  : 500,
            },
            'abyss-radio-root': {
              padding: '12px 0',
            },
          }}
          label={t(
            'FACILITY_DETAILS.PHYSICIAN_DIRECTORY_TAB.SORT_POPOVER.PROVIDER_DESCENDING'
          )}
          value={PhysicianDirectorySortOrder.DESCENDING}
        />
      </RadioGroup>
      <Button
        data-auto-testid={sortBtnName}
        data-testid={sortBtnName}
        onClick={() => {
          setSortOrder(temporarySortOrder);
          setIsOpen(false);
          adobeLinkTrackEvent({
            name: `${sortBtnName}`,
            location: `modal:physician sort`,
            type: 'internal',
            destinationUrl: '',
          });
        }}
      >
        {t('Sort')}
      </Button>
    </React.Fragment>
  );
};

export const SortPopover = ({
  setSortOrder,
  sortOrder,
}: {
  setSortOrder: (val: PhysicianDirectorySortOrder) => void;
  sortOrder: PhysicianDirectorySortOrder;
}) => {
  const { t } = useTranslation();
  const desktopScreen = useMediaQuery(notMobile);
  const mobileScreen = useMediaQuery(phoneOnly);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const properties = {
    drawerSize: 'fit-content',
    drawerPosition: 'right',
  };
  const textStyles = {
    'abyss-text-root': {
      backgroundColor: 'transparent',
      borderColor: '$core.color.neutral.30',
      height: '42px',
      width: mobileScreen ? '98px' : 'auto',
      display: 'inline-block',
      border: 'solid 1px $core.color.neutral.30',
      borderRadius: '24px',
      cursor: 'pointer',
      '&:hover': {
        background: '$core.color.brand.20',
      },
      '@media (max-width: 744px)': {
        border: '2px solid $core.color.brand.100',
      },
      padding: '$sm $lg $sm $md',
      fontWeight: '600',
      '& div:first-of-type': {
        marginLeft: 0,
      },
      '& div:last-of-type': {
        marginLeft: 0,
      },
      '& div:last-of-type > span': {
        marginRight: '3px',
        color: mobileScreen ? '$core.color.brand.100' : '#19191A !important',
      },
      '& div svg': {
        fill: mobileScreen ? '$core.color.brand.100' : '#19191A !important',
      },
    },
  };
  const drawerStyles = {
    'abyss-modal-base-content-container': {
      borderRadius: '20px 20px 0px 0px',
      height: 'auto !important',
      padding: '$md',
      '.abyss-radio-root': {
        '&:hover': {
          backgroundColor: '$core.color.aqua.15',
        },
      },
    },
    'abyss-modal-close-button': {
      top: '12px',
      right: '12px',
    },
    'abyss-modal-header-container': {
      padding: '0',
      marginBottom: '$md',
    },
    'abyss-modal-content-title': {
      fontFamily: '$primary !important',
      fontSize: 'medium !important',
      fontWeight: 'bold !important',
      lineHeight: '24px !important',
      color: '$core.color.neutral.80 !important',
      padding: '0',
    },
  };
  return (
    <React.Fragment>
      <Drawer
        css={drawerStyles}
        header=""
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        position="right"
        size={properties.drawerSize}
      >
        <SortingRadioGroup
          setIsOpen={setIsDrawerOpen}
          setSortOrder={setSortOrder}
          sortOrder={sortOrder}
        />
      </Drawer>
      {mobileScreen ? (
        <React.Fragment>
          <Text
            css={textStyles}
            data-auto-testid="physician-directory-sort-button"
            data-testid="physician-directory-sort-button"
            fontWeight="medium"
            onClick={() => setIsDrawerOpen(true)}
            size="$md"
            variant="outline"
          >
            <Layout.Group>
              <IconSymbol css={swapIconStyles} icon="swap_vert" />
              {!desktopScreen && (
                <MobileText color="$core.color.brand.100">
                  {t('Sort')}
                </MobileText>
              )}
              {desktopScreen && (
                <React.Fragment>
                  <DesktopSortText>
                    {sortOrder === PhysicianDirectorySortOrder.ASCENDING
                      ? t(
                          'FACILITY_DETAILS.PHYSICIAN_DIRECTORY_TAB.SORT_POPOVER.PROVIDER_ASCENDING'
                        )
                      : t(
                          'FACILITY_DETAILS.PHYSICIAN_DIRECTORY_TAB.SORT_POPOVER.PROVIDER_DESCENDING'
                        )}
                  </DesktopSortText>
                  <IconSymbol icon="expand_more" />
                </React.Fragment>
              )}
            </Layout.Group>
          </Text>
        </React.Fragment>
      ) : (
        <StyledPopover
          content={
            <SortingRadioGroup
              setIsOpen={setIsPopoverOpen}
              setSortOrder={setSortOrder}
              sortOrder={sortOrder}
            />
          }
          css={{
            zIndex: 10,
            '.abyss-radio-group-label': {
              marginLeft: '$sm',
              marginRight: '16px',
            },
            '.abyss-radio-root': {
              marginLeft: '$sm',
            },
          }}
          data-auto-testid="sort-popover"
          data-testid="sort-popover"
          onOpenChange={() => {
            setIsPopoverOpen(!isPopoverOpen);
          }}
          open={isPopoverOpen}
          position="right"
          title=""
          width="210px"
          zIndex="99"
        >
          <Text
            css={textStyles}
            data-auto-testid="physician-directory-sort-button"
            data-testid="physician-directory-sort-button"
            fontSize="md"
            fontWeight="medium"
            variant="outline"
          >
            <Layout.Group>
              <IconSymbol css={swapIconStyles} icon="swap_vert" />
              {!desktopScreen && (
                <MobileText color="$core.color.brand.100">
                  {t('Sort')}
                </MobileText>
              )}
              {desktopScreen && (
                <React.Fragment>
                  <DesktopSortText>
                    {sortOrder === PhysicianDirectorySortOrder.ASCENDING
                      ? t(
                          'FACILITY_DETAILS.PHYSICIAN_DIRECTORY_TAB.SORT_POPOVER.PROVIDER_ASCENDING'
                        )
                      : t(
                          'FACILITY_DETAILS.PHYSICIAN_DIRECTORY_TAB.SORT_POPOVER.PROVIDER_DESCENDING'
                        )}
                  </DesktopSortText>
                  <IconSymbol icon="expand_more" />
                </React.Fragment>
              )}
            </Layout.Group>
          </Text>
        </StyledPopover>
      )}
    </React.Fragment>
  );
};
